import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import { SidebarProvider } from "./SidebarContext";
import NavItem from "./NavItem";
import SidebarFooter from "../SidebarFooter";
import SidebarForm from "../SidebarForm";
import SidebarHeader from "../SidebarHeader";
import SidebarMinimizerModernized from "../SidebarMinimizer/SidebarMinimizerModernized";
import * as Tooltip from "@radix-ui/react-tooltip";
import nav from "./_nav";
import "../../../scss/components/_sidebar-modernized.scss";

interface SidebarModernizedProps {
  auth?: {
    user?: {
      userPermissions?: Array<{ permission: { name: string } }>;
    };
  };
  authentication?: {
    user?: {
      userPermissions?: Array<{ permission: { name: string } }>;
    };
  };
  location?: {
    pathname: string;
  };
  designVariant?: "dark" | "light" | "brand" | "two-tone";
  compactStyle?: string | null;
  bannerVisible?: boolean;
  bannerHeight?: number;
  toggleMinimizer?: () => void;
}

function debounce(fn: Function, ms: number) {
  let timer: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null as any;
      fn.apply(null, args);
    }, ms);
  };
}

const SidebarModernized: React.FC<SidebarModernizedProps> = ({
  auth: authProp,
  authentication,
  location,
  designVariant = "dark",
  compactStyle = null,
  bannerVisible = false,
  bannerHeight = 0,
  toggleMinimizer,
}) => {
  const [width, setWidth] = useState(window.innerWidth);
  const [height, setHeight] = useState(window.innerHeight);
  const [minimizerToggle, setMinimizerToggle] = useState(false);
  const countRef = useRef(0);

  // Set initial sidebar state based on screen width
  useEffect(() => {
    const isNarrowScreen = window.innerWidth < 1400;
    const hasMinimized = document.body.classList.contains("sidebar-minimized");
    const hasFull = document.body.classList.contains("sidebar-full");

    // On narrow screens, default to minimized if no classes are set
    if (isNarrowScreen && !hasMinimized && !hasFull) {
      document.body.classList.add("sidebar-minimized");
    }
    // On wide screens, default to expanded (remove minimized so sidebar is full)
    else if (!isNarrowScreen) {
      if (hasFull) {
        document.body.classList.remove("sidebar-full");
      }
      if (hasMinimized) {
        document.body.classList.remove("sidebar-minimized");
      }
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight;
      setWidth(newWidth);
      setHeight(newHeight);
      countRef.current = 0;

      // Update sidebar state when crossing the 1400px threshold
      const isNarrowScreen = newWidth < 1400;
      const hasMinimized = document.body.classList.contains("sidebar-minimized");
      const hasFull = document.body.classList.contains("sidebar-full");

      if (isNarrowScreen) {
        // On narrow screens: auto-minimize
        if (!hasMinimized && !hasFull) {
          document.body.classList.add("sidebar-minimized");
        }
      } else {
        // On wide screens: auto-maximize (remove minimized so sidebar expands)
        if (hasMinimized && hasFull) {
          document.body.classList.remove("sidebar-full");
        }
        if (hasMinimized) {
          document.body.classList.remove("sidebar-minimized");
        }
      }
    };

    const debouncedResize = debounce(handleResize, 500);
    window.addEventListener("resize", debouncedResize);
    return () => {
      window.removeEventListener("resize", debouncedResize);
    };
  }, []);

  useEffect(() => {
    const el = document.getElementsByClassName("sidebar-nav");
    if (el[0] && isOverflown(el[0] as HTMLElement)) {
      el[0].classList.add("overflow-sidebar");
    } else if (el[0]) {
      el[0].classList.add("no-overflow-sidebar");
    }
  }, [width, height]);

  const isOverflown = (element: HTMLElement): boolean => {
    let iconHeight = 38;
    let companyHeight = 38;

    if (
      width <= 1400 ||
      (width <= 1400 && !document.body.classList.contains("sidebar-full")) ||
      document.body.classList.contains("sidebar-minimized")
    ) {
      iconHeight = 36;
      companyHeight = 0;
    }

    const heightOfIcons = iconHeight * (countRef.current + 1) + 55 + companyHeight;
    return element.scrollHeight > heightOfIcons;
  };

  const handleToggleMinimizer = () => {
    setMinimizerToggle((prev) => !prev);
    if (toggleMinimizer) {
      toggleMinimizer();
    }
  };

  const sidebarStyle =
    bannerVisible && bannerHeight > 0
      ? {
          height: `calc(100vh - ${bannerHeight}px - 55px)`,
        }
      : {};

  const auth = authProp ?? authentication;

  return (
    <SidebarProvider
      compactStyle={compactStyle}
      location={location}
      auth={auth}
    >
      <div
        className={`sidebar sidebar-modernized sidebar-modernized-${designVariant}${compactStyle ? ` sidebar-compact-${compactStyle}` : ""}`}
        style={sidebarStyle}
        data-compact-style={compactStyle || undefined}
      >
        <SidebarHeader />
        <SidebarForm />

        <Tooltip.Provider delayDuration={300} skipDelayDuration={200}>
          <nav
            className="sidebar-nav"
            style={
              compactStyle === "option3"
                ? {
                    padding: "0.5rem 0 !important",
                  }
                : undefined
            }
          >
            <ul className="nav">
              {nav.items.map((item, index) => {
                countRef.current++;
                const uniqueKey = `item-${index}`;
                return <NavItem key={uniqueKey} item={item} itemKey={uniqueKey} />;
              })}
            </ul>
          </nav>
        </Tooltip.Provider>

        <SidebarFooter />
        <SidebarMinimizerModernized toggleMinimizer={handleToggleMinimizer} />
      </div>
    </SidebarProvider>
  );
};

function mapStateToProps(state: any) {
  const { authentication } = state;
  return {
    auth: authentication,
  };
}

const connectedSidebarModernized = connect(mapStateToProps)(SidebarModernized);
export default connectedSidebarModernized;
